// ============================================
// src/components/maintenance/MaintenanceCalendar.js
// Mobile-Optimized Calendar View
// ============================================

import React, { useState } from 'react';

function MaintenanceCalendar({ tasks, onTaskClick, onDateClick }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // month, week

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Get calendar days for current month
  const getCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    const current = new Date(startDate);
    
    while (current <= lastDay || current.getDay() !== 0) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  };

  // Get tasks for a specific date
  const getTasksForDate = (date) => {
    return tasks.filter(task => {
      const taskDate = new Date(task.scheduled_date);
      return taskDate.toDateString() === date.toDateString();
    });
  };

  // Navigate months
  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Check if date is in current month
  const isCurrentMonth = (date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  const calendarDays = getCalendarDays();

  return (
    <div style={{
      background: 'white',
      borderRadius: '10px',
      padding: '20px',
      boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
    }}>
      {/* Calendar Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <button
          onClick={() => navigateMonth(-1)}
          style={navButtonStyle}
        >
          ‹
        </button>
        
        <h3 style={{
          margin: 0,
          fontSize: '18px',
          color: '#333'
        }}>
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h3>
        
        <button
          onClick={() => navigateMonth(1)}
          style={navButtonStyle}
        >
          ›
        </button>
      </div>

      {/* Today Button */}
      <div style={{
        textAlign: 'center',
        marginBottom: '15px'
      }}>
        <button
          onClick={() => setCurrentDate(new Date())}
          style={{
            padding: '8px 20px',
            background: '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '20px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600'
          }}
        >
          Today
        </button>
      </div>

      {/* Week Days Header */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: '5px',
        marginBottom: '10px'
      }}>
        {weekDays.map(day => (
          <div
            key={day}
            style={{
              textAlign: 'center',
              fontSize: '12px',
              fontWeight: '600',
              color: '#999',
              padding: '5px'
            }}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: '5px'
      }}>
        {calendarDays.map((date, index) => {
          const dayTasks = getTasksForDate(date);
          const hasOverdue = dayTasks.some(t => t.isOverdue);
          
          return (
            <div
              key={index}
              onClick={() => onDateClick && onDateClick(date)}
              style={{
                minHeight: '80px',
                padding: '8px',
                border: isToday(date) ? '2px solid #667eea' : '1px solid #e0e0e0',
                borderRadius: '8px',
                background: isToday(date) ? '#f0f4ff' : 
                          !isCurrentMonth(date) ? '#f8f9fa' : 
                          'white',
                cursor: 'pointer',
                position: 'relative',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (dayTasks.length > 0) {
                  e.currentTarget.style.transform = 'scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Date Number */}
              <div style={{
                fontSize: '14px',
                fontWeight: isToday(date) ? 'bold' : '500',
                color: !isCurrentMonth(date) ? '#ccc' : '#333',
                marginBottom: '5px'
              }}>
                {date.getDate()}
              </div>

              {/* Task Indicators */}
              {dayTasks.length > 0 && (
                <div style={{ fontSize: '11px' }}>
                  {dayTasks.slice(0, 2).map((task, taskIndex) => (
                    <div
                      key={taskIndex}
                      onClick={(e) => {
                        e.stopPropagation();
                        onTaskClick(task);
                      }}
                      style={{
                        background: task.isOverdue ? '#dc3545' :
                                  task.status === 'completed' ? '#28a745' :
                                  task.status === 'in_progress' ? '#17a2b8' :
                                  '#667eea',
                        color: 'white',
                        padding: '2px 4px',
                        borderRadius: '3px',
                        marginBottom: '2px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        cursor: 'pointer'
                      }}
                    >
                      {task.maintenance_categories?.icon} {task.title.substring(0, 10)}...
                    </div>
                  ))}
                  {dayTasks.length > 2 && (
                    <div style={{
                      color: '#999',
                      fontSize: '10px',
                      textAlign: 'center'
                    }}>
                      +{dayTasks.length - 2} more
                    </div>
                  )}
                </div>
              )}

              {/* Overdue Indicator */}
              {hasOverdue && (
                <div style={{
                  position: 'absolute',
                  top: '5px',
                  right: '5px',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: '#dc3545',
                  animation: 'pulse 2s infinite'
                }}></div>
              )}
            </div>
          );
        })}
      </div>

      {/* Task Summary for Selected Month */}
      <div style={{
        marginTop: '30px',
        padding: '20px',
        background: '#f8f9fa',
        borderRadius: '8px'
      }}>
        <h4 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#333' }}>
          Month Summary
        </h4>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '15px'
        }}>
          <SummaryCard
            label="Total Tasks"
            value={tasks.filter(t => {
              const taskDate = new Date(t.scheduled_date);
              return taskDate.getMonth() === currentDate.getMonth() &&
                     taskDate.getFullYear() === currentDate.getFullYear();
            }).length}
            color="#667eea"
          />
          <SummaryCard
            label="Completed"
            value={tasks.filter(t => {
              const taskDate = new Date(t.scheduled_date);
              return taskDate.getMonth() === currentDate.getMonth() &&
                     taskDate.getFullYear() === currentDate.getFullYear() &&
                     t.status === 'completed';
            }).length}
            color="#28a745"
          />
          <SummaryCard
            label="Pending"
            value={tasks.filter(t => {
              const taskDate = new Date(t.scheduled_date);
              return taskDate.getMonth() === currentDate.getMonth() &&
                     taskDate.getFullYear() === currentDate.getFullYear() &&
                     t.status === 'scheduled';
            }).length}
            color="#ffc107"
          />
          <SummaryCard
            label="Overdue"
            value={tasks.filter(t => {
              const taskDate = new Date(t.scheduled_date);
              return taskDate.getMonth() === currentDate.getMonth() &&
                     taskDate.getFullYear() === currentDate.getFullYear() &&
                     t.isOverdue;
            }).length}
            color="#dc3545"
          />
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0% {
            box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7);
          }
          70% {
            box-shadow: 0 0 0 10px rgba(220, 53, 69, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(220, 53, 69, 0);
          }
        }
      `}</style>
    </div>
  );
}

// Summary Card Component
function SummaryCard({ label, value, color }) {
  return (
    <div style={{
      background: 'white',
      padding: '15px',
      borderRadius: '8px',
      borderLeft: `4px solid ${color}`
    }}>
      <div style={{
        fontSize: '24px',
        fontWeight: 'bold',
        color: color,
        marginBottom: '5px'
      }}>
        {value}
      </div>
      <div style={{
        fontSize: '12px',
        color: '#666',
        textTransform: 'uppercase'
      }}>
        {label}
      </div>
    </div>
  );
}

const navButtonStyle = {
  padding: '10px 15px',
  background: '#f0f0f0',
  border: 'none',
  borderRadius: '5px',
  cursor: 'pointer',
  fontSize: '20px',
  fontWeight: 'bold',
  color: '#666',
  transition: 'background 0.2s'
};

export default MaintenanceCalendar;